import { z } from 'zod';

import {
  deriveReportingEligibility,
  isLocationReal,
  isMeasuredPowerQualified,
  MIN_MEASURED_POWER_COVERAGE_RATIO,
  MIN_MEASURED_POWER_WINDOW_DAYS,
  type UsePhaseInputProvenance,
  type UsePhaseReportingEligibility,
} from './accuracy-tier.js';
import type { BoundsBasis } from './use-phase-math.js';

/**
 * The subset of `UsePhaseReportingEligibility` reachable from a parsed
 * `input_provenance` — i.e. everything `deriveReportingEligibility` can
 * actually return. Duplicated here (rather than imported) to avoid a
 * circular import with `dto/use-phase.ts` (which already imports THIS
 * module); mirrors that file's own
 * `COMPUTED_USE_PHASE_REPORTING_ELIGIBILITY_VALUES` hardcoded-tuple pattern.
 * The `satisfies`-style check below fails to compile if this ever drifts
 * from `accuracy-tier.ts`'s source enum.
 */
const COMPUTED_REPORTING_ELIGIBILITY_VALUES = [
  'reportable',
  'screening_estimate_default_location',
  'screening_estimate_default_pue',
  'screening_estimate_default_power',
  'screening_estimate_default_power_and_location',
] as const;
type ComputedReportingEligibility = (typeof COMPUTED_REPORTING_ELIGIBILITY_VALUES)[number];
type _ComputedEligibilityIsSubsetOfFull = ComputedReportingEligibility extends UsePhaseReportingEligibility
  ? true
  : never;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _computedEligibilityIsSubsetOfFull: _ComputedEligibilityIsSubsetOfFull = true;

/**
 * `screeningBasis` — a pure, read-time derivation from `UsePhaseInputProvenance`
 * (+ a snapshot's `bounds_basis`) that lists EVERY defaulted term behind a
 * use-phase figure, not just the single tie-broken scalar
 * `deriveReportingEligibility` reports (SIL-380, T? of
 * resources/plans/use-phase-carbon.md).
 *
 * WHY this exists: `deriveReportingEligibility` maps a derived accuracy tier
 * to ONE `UsePhaseReportingEligibility` value. When several inputs are
 * defaulted at once (power coverage AND PUE AND several hardware-identity
 * fields, say), the losing terms are silently dropped by the tie-break rule
 * documented only in a code comment (`accuracy-tier.ts:264-278`, flagged
 * there for product sign-off). A real production asset
 * (328fde0f-d9db-45cb-bdaa-28b948d81811) carries SEVEN defaults at once and
 * `deriveReportingEligibility` reports exactly one of them.
 * `deriveUsePhaseScreeningBasis` below makes the full object the source of
 * truth and `mostMaterial` a documented PROJECTION of it (it literally calls
 * `deriveReportingEligibility` — the tie-break rule lives in exactly one
 * place, unchanged, unrepeated) so every losing term stays on record instead
 * of disappearing.
 *
 * Nothing here is stored — derived at read time from a parsed
 * `input_provenance`, same discipline as `deriveUsePhaseAccuracyTier` /
 * `deriveReportingEligibility` (see `accuracy-tier.ts`'s module header for
 * why: the source row is immutable, so a stored derivation would
 * permanently strand historical rows the moment the rules change).
 *
 * No DB access, no cross-import back into `accuracy-tier.ts` — this module
 * only calls the two exported `accuracy-tier.ts` derive/predicate functions,
 * never reimplements their logic.
 */

export const USE_PHASE_SCREENING_DEFAULT_TERM_VALUES = [
  'power_coverage',
  'power_basis',
  'hardware_identity',
  'pue',
  'location',
  'bounds',
] as const;
export type UsePhaseScreeningDefaultTerm = (typeof USE_PHASE_SCREENING_DEFAULT_TERM_VALUES)[number];

export const usePhaseScreeningDefaultTermSchema = z.enum(USE_PHASE_SCREENING_DEFAULT_TERM_VALUES);

/**
 * One defaulted term behind a use-phase figure.
 *
 * `affectsUsePhaseFigure` — DELIBERATELY NOT NAMED "material": that word has
 * a GHG Protocol threshold meaning ("material" = crosses some quantitative
 * significance bar) that this field does not compute. It is a plain
 * true/false record of whether THIS default actually moved the STORED
 * `gwp_use_annual_kgco2eq` figure, per the table in this module's
 * `deriveUsePhaseScreeningBasis` docstring.
 *
 * *** USE-PHASE-SCOPED ONLY — DO NOT REUSE FOR EMBODIED CARBON ***
 * The `hardware_identity` row's `affectsUsePhaseFigure` is `false` on the
 * measured power path (BoaviztAPI is handed the supplied wattage directly —
 * see the docstring below for the verified evidence). Those SAME
 * hardware-identity defaults DOMINATE the embodied-carbon figure (component
 * bill-of-materials, not power draw, drives `gwp.embedded`). Reusing this
 * flag or this type for an embodied-carbon surface would be flatly wrong —
 * the type name and every doc comment on this field say "UsePhaseFigure",
 * never just "figure", to make that impossible to misread.
 */
export interface UsePhaseScreeningDefault {
  term: UsePhaseScreeningDefaultTerm;
  detail: string;
  /** Whether this default moved the STORED use-phase `gwp.use` figure — use-phase-scoped only, see interface docstring. */
  affectsUsePhaseFigure: boolean;
  remedy?: string;
}

export const usePhaseScreeningDefaultSchema = z.object({
  term: usePhaseScreeningDefaultTermSchema,
  detail: z.string(),
  affectsUsePhaseFigure: z.boolean(),
  remedy: z.string().optional(),
});

/**
 * The full screening-basis object. `mostMaterial` is a PROJECTION, not an
 * independent computation — it is always exactly
 * `deriveReportingEligibility(provenance)`'s return value, so it can never
 * disagree with the existing scalar enum. `defaults` is the additive detail
 * that scalar drops.
 */
export interface UsePhaseScreeningBasis {
  defaults: UsePhaseScreeningDefault[];
  mostMaterial: ComputedReportingEligibility;
}

export const usePhaseScreeningBasisSchema = z.object({
  defaults: z.array(usePhaseScreeningDefaultSchema),
  mostMaterial: z.enum(COMPUTED_REPORTING_ELIGIBILITY_VALUES),
});

/**
 * Derives `screeningBasis` for a computed use-phase figure.
 *
 * `boundsBasis` is passed separately from `provenance` because it lives on
 * the snapshot/figure row, not on `input_provenance` — mirrors the ops
 * panel's existing `boundsFellBack` check
 * (`apps/operations/src/components/UsePhaseDiagnosticPanel.tsx`).
 *
 * `affectsUsePhaseFigure` rule, one row per term:
 *
 * | Term | Present when | `affectsUsePhaseFigure` |
 * |---|---|---|
 * | `power_coverage` | `power.basis = 'telemetry_measured'` but the window/coverage gate fails (`< MIN_MEASURED_POWER_WINDOW_DAYS` days or `< MIN_MEASURED_POWER_COVERAGE_RATIO` coverage) | always `true` — the whole point of this term is that it demoted the tier |
 * | `power_basis` | `power.basis = 'archetype_default'` | always `true` |
 * | `hardware_identity` | one entry per `hardwareIdentityDefaults` key, always present when that map is non-empty | `true` only when `power.basis = 'archetype_default'` — verified against a real BoaviztAPI response: on the measured path BoaviztAPI is given the supplied wattage directly (`avg_power: {value: 238, status: INPUT}`), so a defaulted `cpu.name`/`gpu.archetype` genuinely does not move `gwp.use` in that case; on the archetype path, `/v1/server/`'s wattage model depends on the resolved chassis identity, so it does |
 * | `pue` | `pue.source = 'industry_default'` | always `true` (every Scope-2-labeled surface applies `pue` via `applyPue()`) |
 * | `location` | `location.rung = 'default_wor'` | always `true` (the grid-intensity factor is resolved from this rung) |
 * | `bounds` | `boundsBasis = 'archetype_span'` on a `power.basis = 'telemetry_measured'` snapshot (a true fallback — the same basis on an archetype-power snapshot is expected, not a fallback) | always `true` |
 *
 * @pure
 */
export function deriveUsePhaseScreeningBasis(
  provenance: UsePhaseInputProvenance,
  boundsBasis: BoundsBasis,
): UsePhaseScreeningBasis {
  const defaults: UsePhaseScreeningDefault[] = [];

  if (provenance.power.basis === 'telemetry_measured' && !isMeasuredPowerQualified(provenance.power)) {
    const { calendarDaysInWindow, daysWithData } = provenance.power;
    const coveragePct =
      calendarDaysInWindow > 0 ? Math.round((daysWithData / calendarDaysInWindow) * 100) : 0;
    defaults.push({
      term: 'power_coverage',
      detail:
        `Observation window ${calendarDaysInWindow}d, coverage ${daysWithData}/${calendarDaysInWindow} ` +
        `= ${coveragePct}% — below the required ${MIN_MEASURED_POWER_WINDOW_DAYS}d window at ` +
        `${Math.round(MIN_MEASURED_POWER_COVERAGE_RATIO * 100)}% coverage, so power does not qualify as ` +
        `measured for accuracy-tier purposes even though real telemetry exists.`,
      affectsUsePhaseFigure: true,
      remedy: 'Extend the observation window or improve daily telemetry coverage to re-qualify as measured power.',
    });
  }

  if (provenance.power.basis === 'archetype_default') {
    defaults.push({
      term: 'power_basis',
      detail:
        "No telemetry — power comes entirely from BoaviztAPI's archetype model for this server " +
        "configuration, not this asset's observed draw.",
      affectsUsePhaseFigure: true,
    });
  }

  for (const [field, reason] of Object.entries(provenance.hardwareIdentityDefaults)) {
    defaults.push({
      term: 'hardware_identity',
      detail: `${field}: ${reason}`,
      affectsUsePhaseFigure: provenance.power.basis === 'archetype_default',
    });
  }

  if (provenance.pue.source === 'industry_default') {
    defaults.push({
      term: 'pue',
      detail: `PUE resolved from the published industry default (${provenance.pue.value}), not a customer- or org-supplied facility PUE.`,
      affectsUsePhaseFigure: true,
      remedy: 'Supply a facility (customer_dc) or org-level PUE to replace the industry default.',
    });
  }

  if (!isLocationReal(provenance.location.rung)) {
    defaults.push({
      term: 'location',
      detail:
        'No resolvable location for this asset — the world-average (WOR) grid-intensity floor was used instead of a real country/region factor.',
      affectsUsePhaseFigure: true,
      remedy: 'Resolve a real usage location (region, data-center address, or seller org address) for this asset.',
    });
  }

  if (boundsBasis === 'archetype_span' && provenance.power.basis === 'telemetry_measured') {
    defaults.push({
      term: 'bounds',
      detail:
        'bounds_basis fell back to archetype_span despite measured power — too few non-null daily power ' +
        'samples to compute a defensible observed p10/p90 band.',
      affectsUsePhaseFigure: true,
    });
  }

  return {
    defaults,
    mostMaterial: deriveReportingEligibility(provenance),
  };
}
